/**
 * Maps the engine's wire message (MessageResponse,
 * services/ai-engine/src/controllers/schema.py) to the package's shape.
 * Tool calls are kept in their raw engine shape (callers that render live
 * tool/source parts need them) alongside the derived `sources`/`tools`
 * convenience fields citation display reads from directly.
 */
import { type ChatSource, type EngineToolCall, sourcesFromToolCalls } from "./citations";

export interface EngineMessageUsage {
  input_tokens: number;
  output_tokens: number;
  requests: number;
}

/** The engine's `MessageResponse` shape, as received over the wire. */
export interface EngineMessage {
  id: number | null;
  role: "user" | "assistant";
  content: string;
  tool_calls?: EngineToolCall[] | null;
  reasoning?: string | null;
  usage?: EngineMessageUsage | null;
  /** True when the run behind this turn was cut short (client disconnect, or
   * the clinician hitting stop) — `content` is a partial reply, not a
   * complete answer. */
  interrupted?: boolean;
}

export interface ChatMessageUsage {
  inputTokens: number;
  outputTokens: number;
  requests: number;
}

/** A message, in the package's shape. */
export interface ChatMessage {
  id: number | null;
  role: "user" | "assistant";
  content: string;
  /** Raw persisted tool calls, for callers that render their own tool/source parts. */
  toolCalls?: EngineToolCall[];
  /** Assistant only: persisted thinking trace, rehydrated as a reasoning part. */
  reasoning?: string;
  usage?: ChatMessageUsage | null;
  /** Assistant only: sources cited by rag_search, recovered for reload parity. */
  sources?: ChatSource[];
  /** Assistant only: true when the turn was cut short and `content` is partial. */
  interrupted?: boolean;
}

export function toChatMessage(engine: EngineMessage): ChatMessage {
  const isAssistant = engine.role === "assistant";
  const toolCalls = engine.tool_calls ?? undefined;
  const sources = isAssistant ? sourcesFromToolCalls(toolCalls) : [];

  return {
    id: engine.id,
    role: engine.role,
    content: engine.content,
    ...(toolCalls ? { toolCalls } : {}),
    ...(isAssistant && engine.reasoning ? { reasoning: engine.reasoning } : {}),
    ...(engine.usage
      ? {
          usage: {
            inputTokens: engine.usage.input_tokens,
            outputTokens: engine.usage.output_tokens,
            requests: engine.usage.requests,
          },
        }
      : {}),
    ...(sources.length > 0 ? { sources } : {}),
    ...(isAssistant && engine.interrupted ? { interrupted: true } : {}),
  };
}
